import Export16 from '@carbon/icons-react/es/export/16';
import { SecureRoute } from '@okta/okta-react';
import { PDFExport, PDFExportProps } from '@progress/kendo-react-pdf';
import { Tooltip } from 'antd';
import moment from 'moment';
import React, { FC, useCallback, useRef, useState } from 'react';
import { FullScreen, useFullScreenHandle } from 'react-full-screen';
import { Switch, useRouteMatch } from 'react-router-dom';
import styled from 'styled-components';
import useDeepCompareEffect from 'use-deep-compare-effect';
import { Project } from 'api/project';
import { Button } from 'components/atoms/button';
import { useProjectPlanFilter } from 'data/projects';
import { useTranslation } from 'hooks/use-translation';
import { logger } from 'services';
import { ProjectPlanEmptyState } from './empty-state';
import { ExportDialog, PrintSettings } from './export-modal';
import { Filters } from './filters';
import { ProjectPlanFullscreenToggle } from './fullscreen';
import { ProjectPlanListView } from './list';
import { ExportTimelineButton, ProjectPlanTimelineView } from './timeline';
import { ViewGroup } from './view-group';

const Wrapper = styled.div`
  height: 100%;

  .fullscreen {
    display: flex;
    flex-direction: column;
    height: 100%;
  }
`;

const exportQuery = `?${moment().valueOf()}`;

export const ProjectPlan: FC<{
  project: Project;
}> = ({ project }) => {
  const { projectId, projectName, sourceTemplates } = project;

  const presentText = useTranslation('present.enter');
  const exportText = useTranslation('export');

  const { path } = useRouteMatch();
  const [showPrivateTasks, setShowPrivateTasks] = useState(true);
  const [showTimelineExportDialog, setShowTimelineExportDialog] =
    useState(false);

  const [filters, defaultFilter] = useProjectPlanFilter({
    customer: project.customer,
    partners: project.partners,
  });
  const handle = useFullScreenHandle();
  const [filter, setFilter] = useState(defaultFilter);
  const [currentIndex, setCurrentIndex] = useState(1);
  const sharedPhaseIds = project.projectPhases
    .filter((p) => !p.private)
    .map((p) => p.projectPhaseId);

  const [projectPhaseIds, setProjectPhaseIds] = useState<number[]>([]);

  const handleForward = () => {
    const index = currentIndex + 1;

    setProjectPhaseIds(sharedPhaseIds.slice(0, index));
    setCurrentIndex(index);
  };

  const handleBackward = () => {
    if (currentIndex > 1) {
      const index = currentIndex - 1;

      setProjectPhaseIds(sharedPhaseIds.slice(0, index));
      setCurrentIndex(index);
    }
  };

  useDeepCompareEffect(() => {
    if (handle.active) {
      setProjectPhaseIds(sharedPhaseIds.slice(0, 1));
    } else if (!showPrivateTasks) {
      setProjectPhaseIds(sharedPhaseIds);
    } else {
      setProjectPhaseIds(
        project.projectPhases.map(({ projectPhaseId }) => projectPhaseId),
      );
      setCurrentIndex(1);
    }
  }, [handle.active, showPrivateTasks, project.projectPhases, sharedPhaseIds]);

  /**
   * Export as PDF related
   * */
  const [showPrintModal, setShowPrintModal] = useState<boolean>(false);
  const [kendoSettings, setkendoSettings] = useState<
    AtLeast<PDFExportProps, 'landscape'>
  >({ landscape: false });
  const [exportingPDF, setExportingPDF] = useState(false);

  /**
   * @description Handle print export
   * #1 this is done to fix CORS issue with kendo
   * @returns
   */
  const pdfExportRef = useRef<PDFExport>(null);
  const [isExportUI, setIsExportUI] = useState(false);
  const handlePrint = () => {
    setIsExportUI(true);
    if (pdfExportRef.current) {
      const element = document.querySelector<HTMLElement>(
        '#print-project-plan',
      );

      const images = element?.getElementsByTagName('img') || []; // #1

      if (images.length) {
        Array.from(images).forEach((img) => {
          // eslint-disable-next-line no-param-reassign
          img.src = img.src.endsWith(exportQuery)
            ? img.src
            : img.src + exportQuery;
        });
      }

      setExportingPDF(true);
      pdfExportRef.current.save(() => {
        setIsExportUI(false);
        setShowPrivateTasks(true);
      });
      setExportingPDF(false);
      setShowPrintModal(false);
      if (images.length) {
        Array.from(images).forEach((img) => {
          // eslint-disable-next-line no-param-reassign
          img.src = !img.src.endsWith(exportQuery)
            ? img.src
            : img.src.replace(exportQuery, '');
        });
      }

      return true;
    }
    setIsExportUI(false);
    setShowPrivateTasks(true);

    logger.error('invalid project plan export', {
      error: new Error(`Invalid project plan export ${projectId}`),
    });

    return false;
  };

  const onPrintSettingsChange = useCallback(
    (settings: PrintSettings) => {
      const {
        status,
        responsible,
        orientation,
        showPrivateTasks: exportPrivateTasks,
      } = settings;

      const primaryFilter = filters.primary.find((f) => f.value === status);
      const secondaryFilter = filters.secondary.find(
        (f) => f.value === responsible,
      );

      setFilter({
        primary: primaryFilter ?? filter.primary,
        secondary: secondaryFilter ?? filter.secondary,
      });

      setkendoSettings({ landscape: !!(orientation === 'landscape') });
      setShowPrivateTasks(exportPrivateTasks);
    },
    [filter, filters],
  );

  const onExportDialogCancel = useCallback(
    () => setShowTimelineExportDialog(false),
    [],
  );

  return (
    <Wrapper>
      {projectPhaseIds.length > 0 ? (
        <FullScreen handle={handle}>
          <Filters
            filters={filters}
            selected={filter}
            onChange={setFilter}
            central={<ViewGroup projectId={projectId} />}
            secondaryExtra={
              <>
                <Tooltip title={presentText}>
                  <div>
                    <ProjectPlanFullscreenToggle
                      canBackward={currentIndex > 1}
                      canForward={
                        projectPhaseIds.length !== sharedPhaseIds.length
                      }
                      isFullScreen={handle.active}
                      onBackward={handleBackward}
                      onExitFullScreen={handle.exit}
                      onForward={handleForward}
                      onFullScreen={handle.enter}
                    />
                  </div>
                </Tooltip>
                {!handle.active ? (
                  <Switch>
                    <SecureRoute path={`${path}/timeline`}>
                      <ExportTimelineButton
                        onClick={() => setShowTimelineExportDialog(true)}
                      />
                    </SecureRoute>
                    <SecureRoute path={path}>
                      <Tooltip title={exportText}>
                        <Button
                          icon={<Export16 />}
                          type="text"
                          onClick={() => setShowPrintModal(true)}
                          data-cy="projects_plan.export"
                        />
                      </Tooltip>
                    </SecureRoute>
                  </Switch>
                ) : null}
              </>
            }
          />
          <Switch>
            <SecureRoute path={`${path}/timeline`}>
              <ProjectPlanTimelineView
                filter={filter}
                filters={filters}
                projectId={projectId}
                projectName={projectName}
                handle={handle}
                showExportDialog={showTimelineExportDialog}
                showPrivateTasks={showPrivateTasks}
                onExportDialogCancel={onExportDialogCancel}
                onExportSettingsChange={onPrintSettingsChange}
              />
            </SecureRoute>
            <SecureRoute path={path}>
              <ProjectPlanListView
                filter={filter}
                handle={handle}
                projectId={projectId}
                projectPhaseIds={projectPhaseIds}
                showPrivateTasks={showPrivateTasks}
                sourceTemplates={sourceTemplates}
                pdfExportRef={pdfExportRef}
                isExportUI={isExportUI}
                kendoSettings={kendoSettings}
              />
            </SecureRoute>
          </Switch>
          {showPrintModal ? (
            <ExportDialog
              visible={showPrintModal}
              selected={filter}
              statusFilters={filters.primary}
              responsibleFilters={filters.secondary}
              onOkay={handlePrint}
              onChange={onPrintSettingsChange}
              showPrivateTasks={showPrivateTasks}
              onCancel={() => {
                setShowPrivateTasks(true);
                setShowPrintModal(false);
              }}
              isLoading={exportingPDF}
            />
          ) : null}
        </FullScreen>
      ) : (
        <ProjectPlanEmptyState />
      )}
    </Wrapper>
  );
};
